import React from 'react';
import { Redirect, Route } from 'react-router-dom';
import * as FeatherIcon from 'react-feather';

import { getLoggedInUser, isUserAuthenticated } from '../helpers/authUtils';
import ImageIssueList from '../pages/ImageIssueList';
import ApplicationIssuesList from '../pages/ApplicationIssuesList';
import RepositoryIssueList from '../pages/RepositoryIssuesList';

//images
import aws from '../assets/images/applications-icons/aws.svg';
import k8s from '../assets/images/applications-icons/k8s.svg';

// auth
const Login = React.lazy(() => import('../pages/auth/Login'));
const Logout = React.lazy(() => import('../pages/auth/Logout'));
const Register = React.lazy(() => import('../pages/auth/Register'));
const ForgetPassword = React.lazy(() => import('../pages/auth/ForgetPassword'));
const PasswordReset = React.lazy(() => import('../pages/auth/PasswordReset'));
const Confirm = React.lazy(() => import('../pages/auth/Confirm'));
const EmailVerified = React.lazy(() => import('../pages/auth/EmailVerified'));
const ResendValidation = React.lazy(() => import('../pages/auth/ResendValidation'));
const FrequentlyAskedQuestions = React.lazy(() => import('../pages/auth/FrequentlyAskedQuestions'));
const AskForMfaCode = React.lazy(() => import('../pages/auth/AskForMfaCode'));
const TermsOfService = React.lazy(() => import('../pages/auth/TermsOfService'));
// dashboard
const Dashboard = React.lazy(() => import('../pages/dashboard'));
const KubernetesDashBoard = React.lazy(() => import('../pages/kubernetes'));
const ECSDashboard = React.lazy(() => import('../pages/ecs'));
const RedTeamIssueList = React.lazy(() => import('../pages/RedTeamIssueList'));
const PenTestDashboard = React.lazy(() => import('../pages/PentestDashboard'));
const AssetDashboard = React.lazy(() => import('../pages/AssetDashboard'));
const ThreatDashboard = React.lazy(() => import('../pages/ThreatDashboard'));

//info

const ServiceInfo = React.lazy(() => import('../pages/services'));

const BitBucketRedirect = React.lazy(() => import('../components/BitBucketRedirect'));
const GetStarted = React.lazy(() => import('../pages/setup/GetStarted'));
const ExistingUserWizard = React.lazy(() => import('../pages/setup/Wizard/ExistingUserWizard'));
const Success = React.lazy(() => import('../pages/setup/Success'));

const Cloudsploit = React.lazy(() => import('../pages/cloudsploit_aws'));
const CloudsploitGCP = React.lazy(() => import('../pages/cloudsploit_gcp'));
const CloudsploitAzure = React.lazy(() => import('../pages/cloudsploit_azure'));
const CloudsploitOracle = React.lazy(() => import('../pages/cloudsploit_oracle'));

const RedTeamForm = React.lazy(() => import('../pages/red_teaming/index'));
const RedTeamReportGenerator = React.lazy(() => import('../pages/RedTeamReport/index'));
const IssuesManagement = React.lazy(() => import('../pages/IssuesManagement/index'));

const UserSettings = React.lazy(() => import('../pages/user_settings'));
const ErrorPage = React.lazy(() => import('../pages/error/ErrorPage'));

const GitlabSetup = React.lazy(() => import('../pages/setup/GitLab'));

const ButtonTest = React.lazy(() => import('../components/SelectionCards'));
const ComplianceReport = React.lazy(() => import('../pages/compliance'));
const UserManagement = React.lazy(() => import('../pages/UserManagement'));

const Documentation = React.lazy(() => import('../pages/documentation'));

const UploadClientDocuments = React.lazy(() => import('../pages/UploadClientDocuments'));
const ContinuousPentesting = React.lazy(() => import('../pages/ContinuousPentesting'));

const CredentialsMonitoring = React.lazy(() => import('../pages/CredentialsMonitoring'));
const FixCredentials = React.lazy(() => import('../pages/CredentialsMonitoring/FixCredentials'));

const ScanStats = React.lazy(() => import('../pages/ScanStats'));

const NucleiTemplates = React.lazy(() => import('../pages/NucleiTemplates'));

// third party logins

const GoogleRedirect = React.lazy(() => import('../pages/GoogleRedirect'));
const GithubRedirect = React.lazy(() => import('../pages/GithubRedirect'));
const BitBucketAuthRedirect = React.lazy(() => import('../pages/BitBucketRedirect'));
const AzureAuthRedirect = React.lazy(() => import('../pages/AzureRedirect'));
const RepositoryCallback = React.lazy(() => import('../pages/RepositoryCallback'));

// Solution Dashboard

const SolutionDashboard = React.lazy(() => import('../pages/SolutionDashboard'));

// Networking Dashboard

const NetworkDashboard = React.lazy(() => import('../pages/networkDashboard'));

// CloudGuard Dashboard
const CloudGuardDashboard = React.lazy(() => import('../pages/CloudGuard'));

// Issues List

const CloudIssuesList = React.lazy(() => import('../pages/CloudIssuesList'));

// DDOS

const DDOSComponent = React.lazy(() => import('../pages/DDOSDetection'));

// Phishing

//const PhishingComponent = React.lazy(() => import('../pages/Phishing'));
//const PhishingReportComponent = React.lazy(() => import('../pages/PhishingReport'));

// Calendar

const CalendarComponent = React.lazy(() => import('../pages/Calendar'));

// handle auth and authorization
const PrivateRoute = ({ component: Component, roles, ...rest }) => (
    <Route
        {...rest}
        render={(props) => {
            const loggedInUser = getLoggedInUser();

            if (loggedInUser?.askForMfa) {
                if (props?.location?.pathname !== '/account/mfa-code') {
                    return <Redirect to="/account/mfa-code" />;
                }
            } else if (!isUserAuthenticated()) {
                // not logged in so redirect to login page with the return url
                return <Redirect to={{ pathname: '/account/login', state: { from: props.location } }} />;
            } else {
                const checkUrlRedirectsTo = [
                    '/setup/get-started',
                    '/setup/new-user',
                    '/setup/success',
                    '/repository/callback',
                    '/repository/callback/bitbucket',
                ];

                let userData = getLoggedInUser();
                if (
                    !userData?.account?.organization &&
                    !['IU', 'SA'].includes(userData?.role) &&
                    !checkUrlRedirectsTo.includes(props?.location?.pathname)
                )
                    return <Redirect to="/setup/get-started" />;
            }

            // check if route is restricted by role
            if (roles && roles.indexOf(loggedInUser?.role) === -1) {
                // role not authorised so redirect to home page'
                return <Redirect to={{ pathname: '/404' }} />;
            }

            // authorised so return component
            return <Component {...props} />;
        }}
    />
);

// root routes
const rootRoute = {
    path: '/',
    exact: true,
    component: () => <Redirect to="/dashboard" />,
    route: PrivateRoute,
};

// third party login

const googleRedirect = {
    path: '/sign-up/gmail',
    component: GoogleRedirect,
    route: Route,
};

const githubRedirect = {
    path: '/sign-up/github',
    component: GithubRedirect,
    route: Route,
};

const bitbucketAuthRedirect = {
    path: '/sign-up/bitbucket',
    component: BitBucketAuthRedirect,
    route: Route,
};

const azureAuthRedirect = {
    path: '/sign-up/azure',
    component: AzureAuthRedirect,
    route: Route,
};

const bitBucketRoute = {
    path: '/connect/bitbucket',
    component: BitBucketRedirect,
    route: PrivateRoute,
    roles: ['CA', 'CR'],
};

// dashboards
const dashboardRoute = {
    path: '/setup/new-service',
    name: 'Secure a New Service',
    icon: 'icon-plus_square',
    component: () => <ExistingUserWizard newOrganization={true} />,
    special: true,
    route: PrivateRoute,
    roles: ['CA'],
};

const networkDashboardRoute = {
    path: '/network/dashboard',
    name: 'Network',
    icon: 'icon-path',
    route: PrivateRoute,
    component: NetworkDashboard,
    roles: ['CA', 'CR'],
    feature: 'network_dashboard'
};

const vulnDetailsRoute = {
    path: '/vulnerabilities/details',
    name: 'Vulnerabilities Details',
    icon: 'icon-error_outline',
    roles: ['CA', 'CR'],
    component: RedTeamIssueList,
    route: PrivateRoute,
    feature: 'vulnerabilities_details'
};

const redTeamIssueList = {
    path: '/issue/manager',
    name: 'Vulnerabilities',
    icon: 'icon-error_outline',
    roles: ['IU'],
    component: RedTeamIssueList,
    route: PrivateRoute
};

const complianceRoute = {
    path: '/compliance-report',
    name: 'Compliance center',
    icon: 'icon-bar_chart_square',
    component: ComplianceReport,
    route: PrivateRoute,
    roles: ['CA', 'CR'],
    feature: 'compliance_center'
};

const solutionRoute = {
    path: '/solutions',
    name: 'Recommendation Engine',
    component: SolutionDashboard,
    icon: 'uil-lightbulb-alt',
    route: PrivateRoute,
    roles: ['CA', 'CR'],
    feature: 'recommendation_engine'
};

const appRoutes = [];

// Services info

const serviceInfoRoute = {
    path: '/account/info',
    name: 'Managed services',
    icon: 'icon-data',
    component: ServiceInfo,
    route: PrivateRoute,
    roles: ['CA', 'CR'],
    feature: 'managed_services'
};

// pages
const pagesRoutes = {
    path: '/dashboard',
    name: 'Home',
    icon: 'icon-home_outline',
    iconActive: 'icon-home_fill',
    component: Dashboard,
    route: PrivateRoute,
    roles: ['CA', 'CR'],
};

const userSettingsRoute = {
    path: '/settings',
    name: 'Settings',
    icon: 'icon-settings_filled',
    component: UserSettings,
    route: PrivateRoute,
    roles: ['IU', 'CA', 'CR'],
};

const redTeamEntry = {
    path: '/red-team',
    name: 'Pentesting Team',
    component: RedTeamForm,
    icon: 'icon-calendar_check',
    roles: ['IU'],
    route: PrivateRoute,
};

const issuesManagementRoute = {
    path: '/issues-management',
    name: 'General Issues',
    component: IssuesManagement,
    icon: 'uil-exclamation-triangle',
    roles: ['IU'],
    route: PrivateRoute,
};

const uploadClientDocumentsRoute = {
    path: '/client-documents',
    name: 'Client Documents',
    component: UploadClientDocuments,
    icon: 'icon-file_new',
    roles: ['IU'],
    route: PrivateRoute,
};

const continuousPentestingRoute = {
    path: '/continuous-pentesting',
    name: 'Continuous Pentesting',
    component: ContinuousPentesting,
    icon: 'icon-github',
    roles: ['IU'],
    route: PrivateRoute,
};

const redTeamReportGen = {
    path: '/pentest/report',
    name: 'Penetration Test Report',
    component: RedTeamReportGenerator,
    roles: ['IU'],
    route: PrivateRoute,
};

const gitlabSetup = {
    path: '/gitlab-setup',
    name: 'Gitlab Setup',
    component: GitlabSetup,
    route: PrivateRoute,
    roles: ['CA', 'CR'],
};

// components
const dashboards = {
    path: '/infrastructure/kubernetes',
    header: 'DASHBOARDS',
    name: 'Kubernetes',
    identifier: 'K8S',
    component: KubernetesDashBoard,
    image: k8s,
    route: PrivateRoute,
    roles: ['CA', 'CR'],
    feature: 'k8s_dashboard'
};

const ecsDashboard = {
    path: '/infrastructure/ecs',
    name: 'ECS',
    identifier: 'ECS',
    component: ECSDashboard, // todo create an actual component for ECS
    icon: 'icon-dashboard_02',
    route: PrivateRoute,
    roles: ['CA', 'CR'],
    feature: 'ecs_dashboard'
};

const awsDashboard = {
    path: '/applications/dashboard/aws',
    name: 'AWS',
    component: Cloudsploit,
    identifier: 'AW',
    image: aws,
    route: PrivateRoute,
    roles: ['CA', 'CR'],
    feature: 'aws_dashboard'
};
const gcpDashboad = {
    path: '/applications/dashboard/gcp',
    name: 'GCP',
    component: CloudsploitGCP,
    identifier: 'GC',
    icon: 'icon-dashboard_02',
    route: PrivateRoute,
    roles: ['CA', 'CR'],
    feature: 'gcp_dashboard'
};
const azureDashboard = {
    path: '/applications/dashboard/azure',
    name: 'Azure',
    component: CloudsploitAzure,
    identifier: 'AZ',
    icon: 'icon-dashboard_02',
    route: PrivateRoute,
    roles: ['CA', 'CR'],
    feature: 'azure_dashboard'
};
const oracleDashboard = {
    path: '/applications/dashboard/oracle',
    name: 'Oracle',
    component: CloudsploitOracle,
    identifier: 'OC',
    icon: 'icon-dashboard_02',
    route: PrivateRoute,
    roles: ['CA', 'CR'],
    feature: 'oracle_dashboard'
};

const cloudGuardDashboard = {
    path: '/applications/dashboard/cloud-guard',
    name: 'Oracle Cloud Guard',
    component: CloudGuardDashboard,
    identifier: 'OC',
    icon: 'icon-dashboard_02',
    route: PrivateRoute,
    roles: ['CA', 'CR'],
    feature: 'cloud_guard_dashboard'
}

const pentestDashboard = {
    path: '/applications/dashboard/pentest',
    name: 'Security Assessment',
    icon: 'uil-lock',
    component: PenTestDashboard,
    route: PrivateRoute,
    roles: ['CA', 'CR'],
    feature: 'pentest_dashboard'
};

const assetDashboard = {
    path: '/applications/dashboard/assets',
    name: 'Asset Inventory',
    icon: 'uil-layer-group',
    identifier: 'CLOUD',
    component: AssetDashboard,
    route: PrivateRoute,
    roles: ['CA', 'CR'],
    feature: 'assets_dashboard'
};

const threatDashboard = {
    path: '/threat/dashboard',
    name: 'Threat IQ',
    icon: 'uil-lock',
    component: ThreatDashboard,
    route: PrivateRoute,
    roles: ['CA', 'CR'],
    feature: 'threat_iq_dashboard'
};

const componentsRoutes = {
    path: '/infra',
    name: 'Infrastructure',
    header: 'Infrastructure',
    icon: FeatherIcon.Package,
    children: [
        {
            path: '/infrastructure/kubernetes',
            name: 'Kubernetes',
            identifier: 'K8S',
            component: KubernetesDashBoard,
            route: PrivateRoute,
        },
        {
            path: '/infrastructure/ecs',
            name: 'ECS',
            identifier: 'ECS',
            component: ECSDashboard, // todo create an actual component for ECS
            route: PrivateRoute,
        },
    ],
};

const scanStatsRoute = {
    path: '/scan-stats',
    name: 'Scan Stats',
    component: ScanStats,
    icon: 'icon-bar_chart',
    route: PrivateRoute,
    roles: ['IU'],
};

const nucleiTemplatesRoute = {
    path: '/nuclei-templates',
    name: 'Nuclei Templates',
    component: NucleiTemplates,
    icon: 'uil-notes',
    route: PrivateRoute,
    roles: ['IU'],
};

// forms
const formsRoutes = {
    path: '/forms',
    name: 'Cloud',
    icon: FeatherIcon.Cloud,
    children: [
        {
            path: '/applications/dashboard/aws',
            name: 'AWS Dashboard',
            component: Cloudsploit,
            route: PrivateRoute,
            roles: ['CA', 'CR'],
        },
        {
            path: '/applications/dashboard/gcp',
            name: 'GCP Dashboard',
            component: CloudsploitGCP,
            route: PrivateRoute,
            roles: ['CA', 'CR'],
        },
        {
            path: '/applications/dashboard/azure',
            name: 'Azure Dashboard',
            component: CloudsploitAzure,
            route: PrivateRoute,
            roles: ['CA', 'CR'],
        },
    ],
};

const userManagementRoute = {
    path: '/user/management',
    name: 'User Management',
    icon: 'icon-user',
    component: UserManagement,
    route: PrivateRoute,
    roles: ['CA', 'SA']
};

const errorRoute = {
    path: '*',
    name: 'Error',
    header: 'Not Found',
    component: ErrorPage,
    route: Route,
};

const cardRoute = {
    path: '/testing',
    name: 'Test',
    header: 'Test',
    component: ButtonTest,
    route: PrivateRoute,
};
/*
const phishingRoute = {
    path: '/phishing/campaign',
    name: 'Phishing campaign',
    icon: 'uil-bug',
    component: PhishingComponent,
    route: PrivateRoute
};

const phishingReportRoute = {
    path: '/phishing/report/:campaignId?',
    name: 'Phishing report',
    icon: 'uil-bug',
    component: PhishingReportComponent,
    route: PrivateRoute
};*/

// auth
const authRoutes = {
    path: '/account',
    name: 'Auth',
    children: [
        {
            path: '/account/login',
            name: 'Login',
            component: Login,
            route: Route,
        },
        {
            path: '/account/logout',
            name: 'Logout',
            component: Logout,
            route: Route,
        },
        {
            path: '/account/register',
            name: 'Register',
            component: Register,
            route: Route,
        },
        {
            path: '/account/confirm',
            name: 'Confirm',
            component: Confirm,
            route: Route,
        },
        {
            path: '/account/validate-email',
            name: 'Email Validation',
            component: EmailVerified,
            route: Route,
        },
        {
            path: '/account/forget-password',
            name: 'Forget Password',
            component: ForgetPassword,
            route: Route,
        },
        {
            path: '/account/password-reset',
            name: 'Password Reset',
            component: PasswordReset,
            route: Route,
        },
        {
            path: '/account/resend-validation',
            name: 'Resend validation email',
            component: ResendValidation,
            route: Route,
        },
        {
            path: '/account/faq',
            name: 'Frequently Asked Questions',
            component: FrequentlyAskedQuestions,
            route: Route,
        },
        {
            path: '/account/mfa-code',
            component: AskForMfaCode,
            route: PrivateRoute,
        },
    ],
};

const setupRoutes = {
    path: '/setup/',
    name: 'Auth',
    children: [
        {
            path: '/setup/get-started',
            component: GetStarted,
            route: PrivateRoute,
            roles: ['CA'],
        },
        {
            path: '/setup/new-user',
            component: ExistingUserWizard,
            route: PrivateRoute,
            roles: ['CA'],
        },
        {
            path: '/setup/success',
            component: Success,
            route: PrivateRoute,
            roles: ['CA'],
        },
    ],
};

const termsOfServiceRoute = {
    path: '/terms-of-service',
    component: TermsOfService,
    route: Route
};

// Issues lists

const cloudIssuesRoute = {
    path: '/cloud-issues',
    name: 'Cloud Issues',
    component: CloudIssuesList,
    route: PrivateRoute,
    roles: ['CA', 'CR'],
};

const imageIssuesRoute = {
    path: '/image-issues',
    name: 'Image Issues',
    component: ImageIssueList,
    route: PrivateRoute,
    roles: ['CA', 'CR'],
};

const appIssuesRoute = {
    path: '/application-issues',
    name: 'Application Issues',
    component: ApplicationIssuesList,
    route: PrivateRoute,
    roles: ['CA', 'CR'],
};

const repoIssuesRoute = {
    path: '/repository-issues',
    name: 'Repository Issues',
    component: RepositoryIssueList,
    route: PrivateRoute,
    roles: ['CA', 'CR'],
};

const documentationRoute = {
    path: '/documentation',
    name: 'Documentation',
    component: Documentation,
    route: PrivateRoute,
};

const ddosRoute = {
    path: '/ddos-detection',
    name: 'DDOS Detection',
    component: DDOSComponent,
    route: PrivateRoute,
    roles: ['CA', 'CR'],
};

const calendarRoute = {
    path: '/calendar',
    name: 'Calendar',
    icon: 'icon-calendar_event',
    component: CalendarComponent,
    roles: ['CA', 'CR'],
    route: PrivateRoute,
    feature: 'calendar'
};

const credentialsMonitoringWizardRoute = {
    path: '/fix-credentials',
    name: 'Credentials Monitoring',
    icon: 'uil-monitor',
    component: FixCredentials,
    route: PrivateRoute,
    roles: ['CA', 'CR'],
};
// This one is for the callbacks from github, bitbucket, gitlab
const repositoryCallback = {
    path: '/repository/*',
    component: RepositoryCallback,
    roles: ['CA'],
    route: PrivateRoute,
};

// flatten the list of all nested routes
const flattenRoutes = (routes) => {
    let flatRoutes = [];

    routes = routes || [];
    routes.forEach((item) => {
        flatRoutes.push(item);

        if (typeof item.children !== 'undefined') {
            flatRoutes = [...flatRoutes, ...flattenRoutes(item.children)];
        }
    });
    return flatRoutes;
};

// All routes
const allRoutes = [
    rootRoute,
    pagesRoutes,
    serviceInfoRoute,
    dashboardRoute,
    dashboards,
    ecsDashboard,
    //componentsRoutes,
    //formsRoutes,
    awsDashboard,
    gcpDashboad,
    azureDashboard,
    oracleDashboard,
    //cloudGuardDashboard,
    networkDashboardRoute,
    pentestDashboard,
    assetDashboard,
    threatDashboard,
    authRoutes,
    setupRoutes,
    redTeamEntry,
    issuesManagementRoute,
    uploadClientDocumentsRoute,
    continuousPentestingRoute,
    scanStatsRoute,
    nucleiTemplatesRoute,
    userSettingsRoute,
    gitlabSetup,
    cardRoute,
    bitBucketRoute,
    googleRedirect,
    githubRedirect,
    bitbucketAuthRedirect,
    azureAuthRedirect,
    termsOfServiceRoute,
    complianceRoute,
    vulnDetailsRoute,
    redTeamIssueList,
    documentationRoute,
    userManagementRoute,
    solutionRoute,
    redTeamReportGen,
    cloudIssuesRoute,
    imageIssuesRoute,
    appIssuesRoute,
    repoIssuesRoute,
    ddosRoute,
    calendarRoute,
    /*phishingRoute,
    phishingReportRoute,*/
    credentialsMonitoringWizardRoute,
    repositoryCallback,
    errorRoute,
];

const authProtectedRoutes = [
    pagesRoutes,
    serviceInfoRoute,
    solutionRoute,
    complianceRoute,
    vulnDetailsRoute,
    redTeamIssueList,
    scanStatsRoute,
    nucleiTemplatesRoute,
    userManagementRoute,
    redTeamEntry,
    issuesManagementRoute,
    uploadClientDocumentsRoute,
    continuousPentestingRoute,
    //credentialsMonitoringRoute,
    calendarRoute,
    userSettingsRoute,
    dashboardRoute,
    dashboards,
    ecsDashboard,
    awsDashboard,
    gcpDashboad,
    azureDashboard,
    oracleDashboard,
    //cloudGuardDashboard,
    networkDashboardRoute,
    pentestDashboard,
    assetDashboard,
    threatDashboard,
    ...appRoutes,
    //componentsRoutes,
    //formsRoutes,
];
const allFlattenRoutes = flattenRoutes(allRoutes);
export { allRoutes, authProtectedRoutes, allFlattenRoutes };
